import { useEffect, useState, useContext } from "react";
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { Link, useNavigate } from "react-router";
import useSignUp from '../../hooks/Auth/useSignUp';
import { AuthContext } from "../../context/AuthContext.tsx";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import CountrySelector from "../../components/form/input/CountrySelector";
import Checkbox from "../form/input/Checkbox";
import COUNTRIES from "../../lib/countries/countries.json";
import TermsAndConditions from "../common/TermsAndConditions";
import PrivacyPolicy from "../common/PrivacyPolicy";
import Button from "../ui/button/Button";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../../context/ToastContext";
import { useStoreSelector } from '../../hooks/useStoreFromContext';
import { EyeCloseIcon, EyeIcon } from "../../icons";
import { findCountryByValue } from '../../lib/countries/countries.utils'
import { Modal } from "../ui/modal";
import { readStoredToken } from "../../apollo/client";

import type { AppState } from '../../store';
import type { SignUpFormValues } from '../../types/signUpTypes';

const SignUpForm = () => {
  const toast = useToast();
  // const defaultCountry = COUNTRIES[0] // Use country from IP as default
  const { control, handleSubmit } = useForm<SignUpFormValues>();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [country, setCountry] = useState<typeof COUNTRIES[number] | string>(''); // remove string after find by IP
  const [showPassword, setShowPassword] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [CountrySelectorIsOpen, setCountrySelectorIsOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { isOpen: isTermsOpen, openModal: openTerms, closeModal: closeTerms } = useModal();
  const { isOpen: isPrivacyOpen, openModal: openPrivacy, closeModal: closePrivacy } = useModal();
  const { signUp } = useSignUp({
    onCompleted: (user) => {
      if (user) {
        setUser(user);
        const storedToken = readStoredToken();
        if (storedToken?.['access-token']) {
          auth?.login();
          setShouldRedirect(true);
        }
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }, 
    mutationOptions: { awaitRefetchQueries: true }
  });
  const setUser = useStoreSelector((s: AppState) => s.setUser);

  const onSubmit: SubmitHandler<SignUpFormValues>  = formValues => {
    signUp({ 
      email: formValues.email,
      password: formValues.password, 
      passwordConfirmation: formValues.passwordConfirmation, 
      confirmUrl: 'http://localhost:4000/email_confirmation', 
      firstName: formValues.firstName, 
      lastName: formValues.lastName,
      countryCode: formValues.countryCode
    });
  };

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/dashboard', { replace: true }); 
    }
  }, [shouldRedirect, navigate]); 
  
  return (
    <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Sign Up
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your email and password to sign up!
            </p>
          </div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* <!-- First Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      First Name<span className="text-error-500">*</span>
                    </Label>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{ required: 'First name is required' }}
                      render={({ field, fieldState: { error }}) => (
                        <>
                          <Input
                            type="text"
                            id="firstName"
                            placeholder="Enter your first name"
                            {...field}
                          />
                          {error && <span className='text-sm text-error-500'>{error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                  {/* <!-- Last Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      Last Name<span className="text-error-500">*</span>
                    </Label>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{ required: 'Last name is required' }}
                      render={({ field, fieldState: { error }}) => (
                        <>
                          <Input
                            type="text"
                            id="lastName"
                            placeholder="Enter your last name"
                            {...field}
                          />
                          {error && <span className='text-sm text-error-500'>{error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                </div>
                {/* <!-- Country --> */}
                <div>
                  <Label>
                    Country<span className="text-error-500">*</span>
                  </Label>
                  <Controller
                    name="countryCode"
                    control={control}
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error }}) => (
                      <>
                        <CountrySelector
                          id={"countryCode"}
                          open={CountrySelectorIsOpen}
                          onToggle={() => setCountrySelectorIsOpen(!CountrySelectorIsOpen)}
                          {...field}
                          onChange={(e) => {
                            const selected = findCountryByValue(e)
                            if (selected) setCountry(selected);
                            field.onChange(e)
                          }}
                          selectedValue={country}
                        />
                        {error && <span className='text-sm text-error-500'>{error.message}</span>}
                      </>
                    )}
                  />
                </div>
                {/* <!-- Email --> */}
                <div>
                  <Label>
                    Email<span className="text-error-500">*</span>
                  </Label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{ required: 'Email is required' }}
                    render={({ field, fieldState: { error }}) => (
                      <>
                        <Input
                          type="email"
                          id="email"
                          placeholder="Enter your email"
                          {...field}
                        />
                        {error && <span className='text-sm text-error-500'>{error.message}</span>}
                      </>
                    )}
                  />
                </div>
                {/* <!-- Password --> */}
                <div>
                  <Label>
                    Password<span className="text-error-500">*</span>
                  </Label>
                  <div>
                    <Controller
                      name="password"
                      control={control}
                      rules={{ required: 'Password is required' }}
                      render={({ field, fieldState: { error }}) => (
                        <>
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              id="password"
                              placeholder="Enter your password"
                              {...field}
                            />
                            <span
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                            >
                              {showPassword ? (
                                <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                              ) : (
                                <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                              )}
                            </span>
                          </div>
                          {error && <span className='text-sm text-error-500'>{error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                </div>
                {/* <!-- Password --> */}
                <div>
                  <Label>
                    Confirmation password<span className="text-error-500">*</span>
                  </Label>
                  <div>
                    <Controller
                      name="passwordConfirmation"
                      control={control}
                      rules={{ required: 'Password confirmation is required' }}
                      render={({ field, fieldState: { error }}) => (
                        <>
                          <div className="relative">
                            <Input
                              type={showPasswordConfirmation ? "text" : "password"}
                              id="passwordConfirmation"
                              placeholder="Enter your password"
                              {...field}
                            />
                            <span
                              onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                              className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                            >
                              {showPasswordConfirmation ? (
                                <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                              ) : (
                                <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                              )}
                            </span>
                          </div>
                          {error && <span className='text-sm text-error-500'>{error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                </div>
                {/* <!-- Checkbox --> */}
                <div className="flex items-center gap-3">
                  <Checkbox
                    className="w-5 h-5"
                    checked={isChecked}
                    onChange={setIsChecked}
                  />
                  <p className="inline-block font-normal text-gray-500 dark:text-gray-400">
                    By creating an account means you agree to the{" "}
                    <span 
                      onClick={openTerms} 
                      className="text-gray-800 dark:text-white/90 cursor-pointer">
                        Terms and Conditions,
                    </span>{" "}
                    and our{" "}
                    <span 
                      onClick={openPrivacy} 
                      className="text-gray-800 dark:text-white cursor-pointer">
                        Privacy Policy
                    </span>
                  </p>
                </div>
                {/* <!-- Button --> */}
                <div>
                  <Button 
                    className="w-full flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    size="md"
                    disabled={!isChecked}
                  >
                    Sign Up
                  </Button>
                </div>
              </div>
            </form>
            <div className="mt-5">
              <p className="text-md font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Already have an account? 
                {'  '}
                <Link
                  to="/signin"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isPrivacyOpen}
        onClose={closePrivacy}
        showCloseButton
        className="max-w-[800px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <PrivacyPolicy />
      </Modal>
      <Modal
        isOpen={isTermsOpen}
        onClose={closeTerms}
        showCloseButton
        className="max-w-[800px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <TermsAndConditions />
      </Modal>
    </div>
  );
}

export default SignUpForm;
